import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { ArrowLeft, Download, ClipboardCheck, Copy, Check, Link2, ChevronDown, XCircle, Users, Loader2, Trash2, Eye } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { QuestionField, isVisible, SpecifyInputs } from './SurveyFillPage';
import { MultiSelectDropdown, EmployeeMultiSelect, filterEmployees } from './SurveyBuilderPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Multi-colour ombre gradient pairs (light → deep) inspired by JotForm/SurveyMonkey
const OMBRE = [
  ['#F97316', '#EF4444'], ['#06B6D4', '#3B82F6'], ['#14B8A6', '#10B981'],
  ['#D946EF', '#8B5CF6'], ['#FCD34D', '#F59E0B'], ['#F472B6', '#EC4899'],
];

function OmbreDefs({ vertical = true }) {
  return (
    <defs>
      {OMBRE.map((pair, i) => (
        <linearGradient key={i} id={`ombre-${i}`} x1="0" y1="0" x2={vertical ? '0' : '1'} y2={vertical ? '1' : '0'}>
          <stop offset="0%" stopColor={pair[0]} />
          <stop offset="100%" stopColor={pair[1]} />
        </linearGradient>
      ))}
    </defs>
  );
}
const ombreFill = (i) => `url(#ombre-${i % OMBRE.length})`;

// Format any answer value for display (handles file objects, matrix dicts, arrays)
function fmtAnswer(v) {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') {
    if (v.url) return v.filename || v.url; // file upload
    // matrix: { row: choice | [choices] }
    return Object.entries(v).map(([k, val]) => `${k}: ${Array.isArray(val) ? val.join('/') : val}`).join(' · ');
  }
  return String(v);
}

export default function SurveyDetailPage() {
  const { surveyId } = useParams();
  const { getAuthHeader, sessionToken } = useAuth();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [responsesData, setResponsesData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [audits, setAudits] = useState([]);
  const [people, setPeople] = useState(null);
  const [tab, setTab] = useState('overview');
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const s = await fetch(`${API}/surveys/${surveyId}`, { headers: getAuthHeader() });
    if (s.ok) setSurvey(await s.json());
    const r = await fetch(`${API}/surveys/${surveyId}/responses`, { headers: getAuthHeader() });
    if (r.ok) setResponsesData(await r.json());
    const a = await fetch(`${API}/surveys/${surveyId}/analytics`, { headers: getAuthHeader() });
    if (a.ok) setAnalytics(await a.json());
    const ad = await fetch(`${API}/surveys/${surveyId}/audits`, { headers: getAuthHeader() });
    if (ad.ok) setAudits(await ad.json());
    const pp = await fetch(`${API}/surveys/${surveyId}/people`, { headers: getAuthHeader() });
    if (pp.ok) setPeople(await pp.json());
  }, [surveyId, getAuthHeader]);
  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!responsesData?.responses?.length) { toast.error('No responses to export'); return; }
    const survey = responsesData.survey;
    const isAnon = survey.is_anonymous;
    const qs = (survey.questions || []).filter(q => q.type !== 'section');
    const headers = [
      'Submitted At',
      ...(isAnon ? [] : ['Respondent', 'Emp Code', 'Department', 'Location', 'Entity', 'Designation']),
      ...qs.map(q => q.label),
    ];
    const rows = responsesData.responses.map(r => [
      r.submitted_at?.slice(0, 19)?.replace('T', ' '),
      ...(isAnon ? [] : [r.respondent_name, r.employee_code, r.department, r.location, r.entity, r.designation]),
      ...qs.map(q => {
        const v = r.answers?.[q.id];
        return fmtAnswer(v);
      }),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${survey.survey_id}-responses.csv`;
    link.click();
  };

  const deleteSurvey = async () => {
    setDeleting(true);
    try {
      const r = await fetch(`${API}/surveys/${surveyId}`, { method: 'DELETE', headers: getAuthHeader() });
      if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed to delete survey'); return; }
      toast.success('Survey deleted');
      navigate('/surveys');
    } finally { setDeleting(false); }
  };


  const closeSurvey = async () => {
    setClosing(true);
    try {
      const r = await fetch(`${API}/surveys/${surveyId}/close`, { method: 'POST', headers: getAuthHeader() });
      if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed to close survey'); return; }
      toast.success('Survey closed');
      setConfirmClose(false);
      await load();
    } finally { setClosing(false); }
  };

  if (!survey) return <Layout><Card><CardContent className="py-10 text-center">Loading…</CardContent></Card></Layout>;

  return (
    <Layout>
      <div className="space-y-4 max-w-6xl mx-auto" data-testid="survey-detail-page">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/surveys')}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
          <div className="flex gap-2">
            {survey.can_edit && (survey.effective_status === 'Draft' || survey.effective_status === 'Scheduled') && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/surveys/${surveyId}/edit`)} data-testid="edit-survey-btn">Edit</Button>
            )}
            {survey.can_edit && (survey.effective_status === 'Active' || survey.effective_status === 'Scheduled') && (
              <Button variant="outline" size="sm" onClick={() => setManageOpen(true)} data-testid="manage-recipients-btn">
                <Users className="h-4 w-4 mr-1" />Manage Recipients
              </Button>
            )}
            {survey.can_edit && survey.effective_status === 'Active' && (
              <Button variant="outline" size="sm" onClick={() => setConfirmClose(true)} data-testid="close-survey-btn" className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
                <XCircle className="h-4 w-4 mr-1" />Close Survey
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
            {survey.can_delete && (
              <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)} data-testid="delete-survey-btn" className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-1" />Delete
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {survey.title}
                  <Badge>{survey.effective_status}</Badge>
                </CardTitle>
                <CardDescription className="mt-1">{survey.description || '—'}</CardDescription>
                <p className="text-xs text-muted-foreground mt-1">Owner: {survey.created_by_name} · Visibility: {survey.visibility} · {survey.questions?.length || 0} questions</p>
              </div>
            </div>
          </CardHeader>
        </Card>

        {survey.effective_status === 'Active' && <ShareUrlCard surveyId={surveyId} />}
        {survey.can_edit && (survey.effective_status === 'Draft' || survey.effective_status === 'Scheduled') && <PreviewLinkCard surveyId={surveyId} />}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="preview" data-testid="tab-preview">Preview</TabsTrigger>
            <TabsTrigger value="people" data-testid="tab-people">People ({people?.total ?? 0})</TabsTrigger>
            <TabsTrigger value="responses" data-testid="tab-responses">Responses ({responsesData?.responses?.length || 0})</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="audit">Audit</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <Card><CardContent className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Stat label="Total Responses" value={analytics?.total_responses ?? 0} />
              <Stat label="Audience" value={analytics?.audience_count ?? 0} />
              <Stat label="Pending" value={analytics?.pending ?? 0} />
              <Stat label="Response Rate" value={`${analytics?.response_rate ?? 0}%`} />
            </CardContent></Card>
            <Card className="mt-4"><CardHeader><CardTitle className="text-base">Questions</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {(survey.questions || []).map(q => (
                  <div key={q.id} className="text-sm border-b pb-2">
                    <span className="font-medium">{q.label || '(untitled)'}</span> {q.required && <span className="text-destructive">*</span>}
                    <Badge variant="outline" className="ml-2 text-[10px]">{q.type}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="preview" className="mt-4">
            <DetailPreview survey={survey} sessionToken={sessionToken} getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="people" className="mt-4">
            <PeoplePane people={people} />
          </TabsContent>

          <TabsContent value="responses" className="mt-4">
            <Card>
              <CardContent className="pt-4">
                {(!responsesData?.responses?.length) ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">No responses yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Submitted</TableHead>
                          {!survey.is_anonymous && <><TableHead>Respondent</TableHead><TableHead>Dept</TableHead><TableHead>Location</TableHead></>}
                          {(survey.questions || []).filter(q => q.type !== 'section').slice(0, 4).map(q => <TableHead key={q.id}>{q.label}</TableHead>)}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {responsesData.responses.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs">{r.submitted_at?.slice(0, 19).replace('T', ' ')}</TableCell>
                            {!survey.is_anonymous && <><TableCell>{r.respondent_name || '—'}</TableCell><TableCell>{r.department || '—'}</TableCell><TableCell>{r.location || '—'}</TableCell></>}
                            {(survey.questions || []).filter(q => q.type !== 'section').slice(0, 4).map(q => (
                              <TableCell key={q.id} className="text-xs">
                                {q.type === 'file' && r.answers?.[q.id]?.url
                                  ? <a href={`${API}/files/${r.answers[q.id].url.split('/').pop()}?token=${(getAuthHeader().Authorization || '').replace('Bearer ', '')}`} target="_blank" rel="noreferrer" className="text-primary underline">{r.answers[q.id].filename || 'file'}</a>
                                  : fmtAnswer(r.answers?.[q.id])}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics" className="mt-4 space-y-4">
            {analytics && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <Stat label="Responses" value={analytics.total_responses} />
                  <Stat label="Response Rate" value={`${analytics.response_rate}%`} />
                  <Stat label="Avg Rating" value={analytics.avg_rating || '—'} />
                  <Stat label="NPS" value={analytics.nps ?? '—'} />
                  <Stat label="Pending" value={analytics.pending} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ChartCard title="By Department" data={analytics.by_department} />
                  <ChartCard title="By Location" data={analytics.by_location} />
                </div>
                {analytics.per_question?.map(q => (
                  <Card key={q.id}>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">{q.label} <Badge variant="outline" className="text-[10px] ml-2">{q.type}</Badge> <span className="text-xs text-muted-foreground ml-2">{q.response_count} responses</span></CardTitle></CardHeader>
                    <CardContent>
                      {q.matrix?.length ? (
                        <MatrixChart matrix={q.matrix} />
                      ) : q.distribution?.length ? (
                        <div style={{ width: '100%', height: 200 }}>
                          <ResponsiveContainer>
                            <BarChart data={q.distribution}>
                              <OmbreDefs />
                              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                              <XAxis dataKey="label" fontSize={12} />
                              <YAxis fontSize={12} allowDecimals={false} />
                              <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                {q.distribution.map((_, i) => <Cell key={i} fill={ombreFill(i)} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : q.average != null ? (
                        <p className="text-2xl font-bold">{q.average}</p>
                      ) : q.text_responses?.length ? (
                        <TextResponsesList items={q.text_responses} />
                      ) : (
                        <p className="text-xs text-muted-foreground">No responses yet.</p>
                      )}
                      {q.specify_responses?.length > 0 && <SpecifyResponsesList items={q.specify_responses} />}
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <Card><CardContent className="pt-4">
              {audits.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">No audit events yet.</p> : (
                <div className="space-y-1 text-sm">
                  {audits.map((a, i) => (
                    <div key={i} className="flex justify-between border-b py-1">
                      <span><Badge variant="outline" className="text-[10px] mr-2">{a.action}</Badge>{a.actor}</span>
                      <span className="text-xs text-muted-foreground">{a.at?.slice(0, 19).replace('T', ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent></Card>
          </TabsContent>
        </Tabs>

        <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
          <AlertDialogContent data-testid="close-survey-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Close this survey?</AlertDialogTitle>
              <AlertDialogDescription>
                Once closed, respondents can no longer submit responses. You can still view all collected responses and analytics. This cannot be undone from here.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="close-survey-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); closeSurvey(); }} disabled={closing}
                className="bg-red-600 hover:bg-red-700" data-testid="close-survey-confirm">
                {closing ? 'Closing…' : 'Close Survey'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent data-testid="delete-survey-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this survey?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently deletes the survey <b>and all of its collected responses</b>. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-survey-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); deleteSurvey(); }} disabled={deleting}
                className="bg-red-600 hover:bg-red-700" data-testid="delete-survey-confirm">
                {deleting ? 'Deleting…' : 'Delete Survey'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <ManageRecipientsDialog open={manageOpen} onOpenChange={setManageOpen} surveyId={surveyId}
          survey={survey} getAuthHeader={getAuthHeader} onSaved={load} />
      </div>
    </Layout>
  );
}

function Stat({ label, value }) {
  return <div><div className="text-2xl font-bold">{value}</div><div className="text-xs text-muted-foreground">{label}</div></div>;
}

function TextResponsesList({ items }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border rounded-lg" data-testid="text-responses">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium hover:bg-muted/40 transition-colors"
        data-testid="text-responses-toggle">
        <span>{items.length} answer{items.length === 1 ? '' : 's'}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul className="px-3 py-2 space-y-2 border-t max-h-80 overflow-y-auto">
          {items.map((it, i) => (
            <li key={i} className="flex items-start justify-between gap-3 text-sm border-b last:border-b-0 pb-2 last:pb-0" data-testid={`text-response-${i}`}>
              <span className="flex-1 whitespace-pre-wrap break-words">{it.text}</span>
              <Badge variant="secondary" className="shrink-0 text-[10px] font-normal">{it.respondent || 'Anonymous'}</Badge>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SpecifyResponsesList({ items }) {
  const [open, setOpen] = useState(false);
  // Group by option
  const groups = {};
  items.forEach(it => { (groups[it.option] = groups[it.option] || []).push(it); });
  return (
    <div className="mt-3 border rounded-lg" data-testid="specify-responses">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium hover:bg-muted/40 transition-colors"
        data-testid="specify-responses-toggle">
        <span>"Specify" answers ({items.length})</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3 py-2 space-y-3 border-t">
          {Object.entries(groups).map(([opt, arr]) => (
            <div key={opt}>
              <p className="text-xs font-semibold text-muted-foreground mb-1">{opt}</p>
              <ul className="space-y-1">
                {arr.map((it, i) => (
                  <li key={i} className="text-sm flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                    <span className="flex-1">{it.text}</span>
                    {it.respondent && <span className="text-[11px] text-muted-foreground shrink-0">— {it.respondent}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ManageRecipientsDialog({ open, onOpenChange, surveyId, survey, getAuthHeader, onSaved }) {
  const [opts, setOpts] = useState({ departments: [], locations: [], entities: [], employees: [] });
  const [aud, setAud] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAud({
      entire_org: survey.audience?.entire_org || false,
      departments: survey.audience?.departments || [],
      locations: survey.audience?.locations || [],
      entities: survey.audience?.entities || [],
      employee_ids: survey.audience?.employee_ids || [],
      grades: survey.audience?.grades || [],
    });
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/surveys/audience-options`, { headers: getAuthHeader() });
        if (r.ok) setOpts(await r.json());
      } catch (_e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [open, survey, getAuthHeader]);

  if (!aud) return null;
  const setList = (k, list) => setAud(a => ({ ...a, [k]: list }));
  const summary = aud.entire_org ? 'Entire organization'
    : `${aud.departments.length} dept · ${aud.locations.length} loc · ${aud.entities.length} entity · ${aud.employee_ids.length} people`;

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/surveys/${surveyId}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ audience: aud }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Failed to update recipients'); return; }
      toast.success('Recipients updated');
      onOpenChange(false);
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="manage-recipients-dialog">
        <DialogHeader>
          <DialogTitle>Manage Recipients</DialogTitle>
          <DialogDescription>
            Update who can access this survey. People you add will see it in their "My Surveys" immediately and can respond right away. Existing responses are unaffected.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <label className="flex items-center gap-2 text-sm">
              <Switch data-testid="manage-entire-org" checked={aud.entire_org} onCheckedChange={v => setAud(a => ({ ...a, entire_org: v }))} />
              Send to Entire Organization
            </label>
            {!aud.entire_org && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MultiSelectDropdown label="Departments" testid="manage-departments" options={opts.departments} selected={aud.departments} onChange={l => setList('departments', l)} />
                <MultiSelectDropdown label="Locations" testid="manage-locations" options={opts.locations} selected={aud.locations} onChange={l => setList('locations', l)} />
                <MultiSelectDropdown label="Entities" testid="manage-entities" options={opts.entities || []} selected={aud.entities} onChange={l => setList('entities', l)} />
                <div className="md:col-span-2">
                  <EmployeeMultiSelect employees={filterEmployees(opts.employees, aud)} selected={aud.employee_ids} onChange={l => setList('employee_ids', l)} />
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground" data-testid="manage-summary">Current selection: {summary}</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="manage-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || loading} data-testid="manage-save">{saving ? 'Saving…' : 'Save Recipients'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewLinkCard({ surveyId }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/surveys/${surveyId}/fill?preview=1`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success('Preview link copied');
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Card data-testid="preview-link-card" className="border-amber-200 bg-amber-50/40">
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-medium">Preview / Test before publishing</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={url} readOnly className="font-mono text-xs" data-testid="preview-url-input" onFocus={e => e.target.select()} />
          <Button onClick={() => window.open(url, '_blank')} className="shrink-0" data-testid="open-preview-btn">
            <Eye className="h-4 w-4 mr-1" />Open Preview
          </Button>
          <Button onClick={copy} variant={copied ? 'default' : 'outline'} className="shrink-0" data-testid="copy-preview-url-btn">
            {copied ? <><Check className="h-4 w-4 mr-1" />Copied</> : <><Copy className="h-4 w-4 mr-1" />Copy</>}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Test the full respondent experience without publishing. Responses submitted in preview are <b>not saved</b>. This link is only usable by you (the creator) and admins.</p>
      </CardContent>
    </Card>
  );
}

function ShareUrlCard({ surveyId }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/surveys/${surveyId}/fill`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success('Link copied');
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Card data-testid="share-url-card">
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 mb-2">
          <Link2 className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Shareable link</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={url} readOnly className="font-mono text-xs" data-testid="share-url-input" onFocus={e => e.target.select()} />
          <Button onClick={copy} variant={copied ? 'default' : 'outline'} className="shrink-0" data-testid="copy-url-btn">
            {copied ? <><Check className="h-4 w-4 mr-1" />Copied</> : <><Copy className="h-4 w-4 mr-1" />Copy</>}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Only people in this survey's audience can open the link — others are asked to sign in and are blocked if they're not in the audience.</p>
      </CardContent>
    </Card>
  );
}

function MatrixChart({ matrix }) {
  // Collect all column labels across rows
  const cols = Array.from(new Set(matrix.flatMap(m => m.distribution.map(d => d.label))));
  const data = matrix.map(m => {
    const row = { row: m.row };
    m.distribution.forEach(d => { row[d.label] = d.count; });
    return row;
  });
  return (
    <div style={{ width: '100%', height: Math.max(160, matrix.length * 60) }}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
          <OmbreDefs vertical={false} />
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
          <XAxis type="number" fontSize={12} allowDecimals={false} />
          <YAxis type="category" dataKey="row" width={120} fontSize={11} />
          <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          <Legend />
          {cols.map((c, i) => <Bar key={c} dataKey={c} stackId="a" fill={ombreFill(i)} radius={[0, 4, 4, 0]} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


function ChartCard({ title, data }) {
  return (
    <Card><CardHeader className="pb-2"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent>
        {!data?.length ? <p className="text-xs text-muted-foreground">No data</p> : (
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <OmbreDefs />
                <Pie data={data} dataKey="count" nameKey="label" outerRadius={80} label>
                  {data.map((_, i) => <Cell key={i} fill={ombreFill(i)} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function PeoplePane({ people }) {
  if (!people) return <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>;
  const badge = (status) => {
    if (status === 'Submitted') return <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">Submitted</Badge>;
    if (status === 'Opened') return <Badge className="bg-amber-500 text-white hover:bg-amber-500">Opened</Badge>;
    return <Badge variant="outline">Pending</Badge>;
  };
  return (
    <Card data-testid="people-pane">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Audience & Participation</CardTitle>
        <div className="flex gap-3 text-sm mt-2">
          <span className="text-muted-foreground">Total: <b className="text-foreground">{people.total}</b></span>
          <span className="text-emerald-700">Submitted: <b>{people.submitted}</b></span>
          <span className="text-amber-600">Opened: <b>{people.opened}</b></span>
          <span className="text-muted-foreground">Pending: <b>{people.pending}</b></span>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {people.people.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">No audience members.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Opened</TableHead>
                  <TableHead>Submitted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {people.people.map(p => (
                  <TableRow key={p.employee_id} data-testid={`people-row-${p.employee_id}`}>
                    <TableCell className="font-medium">{p.full_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.email}</TableCell>
                    <TableCell>{badge(p.status)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.opened_at?.slice(0, 16)?.replace('T', ' ') || '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.submitted_at?.slice(0, 16)?.replace('T', ' ') || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DetailPreview({ survey, sessionToken, getAuthHeader }) {
  const [answers, setAnswers] = React.useState({});
  const [specify, setSpecify] = React.useState({});
  const accent = survey.theme_color || '#E60000';
  const bg = survey.background_color || '#F9FAFB';
  const logoSrc = survey.logo_url ? `${BACKEND_URL}/api/public/survey-logo/${survey.survey_id}` : null;
  let n = 0;
  return (
    <div style={{ backgroundColor: bg }} className="rounded-2xl p-4 sm:p-6" data-testid="detail-preview">
      <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden border bg-white shadow-sm">
        <div className="h-2 w-full" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}66)` }} />
        <div className="p-6 space-y-4">
          {logoSrc && <img src={logoSrc} alt="logo" className="h-16 object-contain mx-auto block" />}
          <h2 className="text-2xl font-bold tracking-tight">{survey.title}</h2>
          {survey.description && <p className="text-sm text-muted-foreground">{survey.description}</p>}
          {(survey.questions || []).map(q => {
            if (q.type === 'section') return <h3 key={q.id} className="text-lg font-semibold border-b pb-1 pt-2">{q.label || 'Section'}</h3>;
            if (!isVisible(q, answers)) return null;
            n += 1;
            return (
              <div key={q.id} className="rounded-xl border p-4">
                <p className="text-sm font-medium">{n}. {q.label} {q.required && <span style={{ color: accent }}>*</span>}</p>
                <div className="mt-2">
                  <QuestionField q={q} value={answers[q.id]} onChange={v => setAnswers(a => ({ ...a, [q.id]: v }))} getAuthHeader={getAuthHeader} accent={accent} />
                  <SpecifyInputs q={q} value={answers[q.id]} specify={specify[q.id]} onChange={(opt, text) => setSpecify(s => ({ ...s, [q.id]: { ...(s[q.id] || {}), [opt]: text } }))} accent={accent} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
